'use client';

import { useRouter, usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useGettingStartedSnapshot } from '@/lib/hooks/use-getting-started-snapshot';
import { GETTING_STARTED_TOTAL_STEPS } from '@/lib/stores';
import { useFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { useGettingStartedDismissal } from '@/lib/services/profile/hooks';

const HUB_PATH = '/onboarding/get-started';
const ONBOARDING_FEATURE_FLAG = 'onboarding';

function ProgressRing({
  completed,
  total,
  size = 16
}: {
  completed: number;
  total: number;
  size?: number;
}) {
  const strokeWidth = 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? completed / total : 0;
  const offset = circumference - progress * circumference;

  return (
    <svg
      width={size}
      height={size}
      className="shrink-0 -rotate-90"
      aria-hidden="true"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-gray-300"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="text-gray-900 transition-all duration-500"
      />
    </svg>
  );
}

export function GettingStartedWidget() {
  const router = useRouter();
  const pathname = usePathname();
  const { features } = useFeatureFlags();
  const { completedCount } = useGettingStartedSnapshot();
  const { isDismissed } = useGettingStartedDismissal();

  // Whole onboarding/setup wizard is gated by the `onboarding` feature flag.
  if (!features.includes(ONBOARDING_FEATURE_FLAG)) return null;

  // Hide only when the user explicitly dismissed the page. Auto-hide on
  // 100% complete was removed in LF-3144 in favour of an explicit Dismiss
  // button on the Getting Started page.
  if (isDismissed) return null;

  const isActive = pathname?.startsWith(HUB_PATH);

  return (
    <Button
      variant="outline"
      size="default"
      className={cn(
        'hover:bg-accent hover:text-accent-foreground w-full justify-center gap-2 px-3 text-sm font-normal',
        isActive && 'bg-accent text-accent-foreground'
      )}
      onClick={() => router.push(HUB_PATH)}
    >
      <ProgressRing
        completed={completedCount}
        total={GETTING_STARTED_TOTAL_STEPS}
      />
      Getting Started
    </Button>
  );
}
